import { useSearchParams } from 'react-router';
import { Async } from '../../components/Async.tsx';
import { FunnelBars } from '../../components/charts/FunnelBars.tsx';
import { BarList } from '../../components/charts/BarList.tsx';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { ImmatureCohortNotice } from '../../components/ImmatureCohortNotice.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import { SegmentedControl, SegmentedItem } from '../../components/ui/toggle-group.tsx';
import { formatNumber, formatPercent } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useFunnel, useLosses, type FunnelResponse } from '../../lib/queries.ts';
import { useDashboardParams } from '../../lib/url-state.ts';
import { Controls } from '../controls/Controls.tsx';

type Mode = 'cohort' | 'activity';
type Data = FunnelResponse['data'];
type Cohort = Extract<Data, { mode: 'cohort' }>;

const OPTIONAL = new Set(['VISIT_SCHEDULED', 'VISIT_COMPLETED']);

function WonPaths({ data }: { data: Data }) {
  const t = useT();
  const paths = data.wonPaths;
  return (
    <Panel title={t.funnel.wonPaths}>
      <p className="mb-2 text-xs text-muted-foreground">{t.funnel.wonPathsHelp}</p>
      <BarList
        ariaLabel={t.funnel.wonPaths}
        unit={t.units.deals}
        items={[
          {
            key: 'completed',
            label: t.funnel.afterCompletedVisit,
            value: paths.afterCompletedVisit,
          },
          {
            key: 'planned',
            label: t.funnel.afterPlannedVisitOnly,
            value: paths.afterPlannedVisitOnly,
          },
          { key: 'none', label: t.funnel.withoutVisit, value: paths.withoutVisit },
        ]}
      />
      {!data.visitAvailability.completed ? (
        <p className="mt-2 text-xs text-muted-foreground">{t.funnel.visitsUnavailable}</p>
      ) : null}
    </Panel>
  );
}

function CohortView({ data }: { data: Cohort }) {
  const t = useT();
  const locale = useLocale();
  const [params] = useDashboardParams();
  const label = (milestone: string): string =>
    t.funnel.steps[milestone as keyof typeof t.funnel.steps];

  const columns: Column<Cohort['steps'][number]>[] = [
    {
      id: 'step',
      header: t.funnel.columns.step,
      cell: (s) => (
        <span>
          {label(s.milestone)}
          {s.optional ? (
            <span className="ml-1 text-xs text-muted-foreground">({t.funnel.optionalStep})</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'count',
      header: t.funnel.columns.count,
      align: 'right',
      cell: (s) => (
        <span>
          {formatNumber(s.count, locale)}
          {s.impliedCount > 0 ? (
            <span className="block text-xs text-muted-foreground">
              {t.funnel.impliedNote(s.impliedCount)}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'ofCohort',
      header: t.funnel.columns.ofCohort,
      align: 'right',
      cell: (s) => formatPercent(s.rateOfCohort, locale),
    },
    {
      id: 'fromPrevious',
      header: t.funnel.columns.fromPrevious,
      align: 'right',
      cell: (s) => formatPercent(s.rateFromPrevious, locale),
    },
  ];

  const timeColumns: Column<Cohort['timeToMilestone'][number]>[] = [
    { id: 'step', header: t.funnel.columns.step, cell: (r) => label(r.milestone) },
    {
      id: 'median',
      header: t.funnel.columns.median,
      align: 'right',
      cell: (r) =>
        r.medianHours === null ? '—' : t.funnel.hours(formatNumber(r.medianHours, locale, 1)),
    },
    {
      id: 'p90',
      header: t.funnel.columns.p90,
      align: 'right',
      cell: (r) =>
        r.p90Hours === null ? '—' : t.funnel.hours(formatNumber(r.p90Hours, locale, 1)),
    },
  ];

  if (data.cohortSize === 0)
    return <EmptyState title={t.common.noData} description={t.funnel.emptyCohort} />;

  return (
    <>
      <ImmatureCohortNotice maturity={data.maturity} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t.funnel.title}>
          <FunnelBars
            steps={data.steps.map((s) => ({
              milestone: s.milestone,
              label: label(s.milestone),
              count: s.count,
              optional: OPTIONAL.has(s.milestone),
            }))}
          />
          <p className="mt-2 text-sm">
            {t.funnel.lost}: <b>{formatNumber(data.lost.count, locale)}</b> · {t.funnel.open}:{' '}
            <b>{formatNumber(data.open.count, locale)}</b>
          </p>
        </Panel>
        <Panel title={t.funnel.conversions}>
          <DataTable
            caption={t.funnel.conversions}
            columns={columns}
            rows={data.steps}
            rowKey={(s) => s.milestone}
          />
        </Panel>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <WonPaths data={data} />
        <Panel title={t.funnel.timeToMilestone}>
          <DataTable
            caption={t.funnel.timeToMilestone}
            columns={timeColumns}
            rows={data.timeToMilestone}
            rowKey={(r) => r.milestone}
          />
        </Panel>
      </div>
      <LossesPanel params={params} />
    </>
  );
}

function LossesPanel({ params }: { params: ReturnType<typeof useDashboardParams>[0] }) {
  const t = useT();
  const [search, setSearch] = useSearchParams();
  const basis = search.get('basis') === 'cohort' ? 'cohort' : 'lost_at';
  const losses = useLosses(params, basis);
  return (
    <Panel
      title={t.funnel.losses}
      actions={
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t.funnel.lossesBasis}</span>
          <SegmentedControl
            aria-label={t.funnel.lossesBasis}
            value={basis}
            onValueChange={(value) => {
              if (!value) return;
              setSearch(
                (current) => {
                  const next = new URLSearchParams(current);
                  next.set('basis', value);
                  return next;
                },
                { replace: true },
              );
            }}
          >
            <SegmentedItem value="lost_at">{t.funnel.basisLostAt}</SegmentedItem>
            <SegmentedItem value="cohort">{t.funnel.basisCohort}</SegmentedItem>
          </SegmentedControl>
        </div>
      }
    >
      <Async query={losses}>
        {({ data }) =>
          data.total === 0 ? (
            <EmptyState title={t.common.noData} />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <BarList
                ariaLabel={t.funnel.losses}
                unit={t.units.deals}
                items={data.byReason.map((r) => ({
                  key: r.reasonId,
                  label: r.name ?? t.common.notSpecified,
                  value: r.count,
                }))}
              />
              <div>
                <p className="mb-2 flex items-center gap-1 text-sm font-medium">
                  {t.funnel.furthest}
                  <InfoPopover label={t.funnel.furthest}>
                    <p>{t.funnel.furthest}</p>
                  </InfoPopover>
                </p>
                <BarList
                  ariaLabel={t.funnel.furthest}
                  unit={t.units.deals}
                  items={data.byFurthestMilestone.map((r) => ({
                    key: r.milestone,
                    label: t.funnel.steps[r.milestone],
                    value: r.count,
                  }))}
                />
              </div>
            </div>
          )
        }
      </Async>
    </Panel>
  );
}

function ActivityView({ data }: { data: Extract<Data, { mode: 'activity' }> }) {
  const t = useT();
  const locale = useLocale();
  return (
    <>
      <div role="note" className="rounded-md border bg-accent p-3 text-sm">
        <p className="font-semibold">{t.funnel.activityNotice}</p>
        <p>{t.funnel.activityNoticeDetail}</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t.funnel.activity}>
          <FunnelBars
            steps={data.steps.map((s) => ({
              milestone: s.milestone,
              label: t.funnel.steps[s.milestone],
              count: s.count,
              optional: OPTIONAL.has(s.milestone),
            }))}
          />
          <ul className="sr-only">
            {data.steps.map((s) => (
              <li key={s.milestone}>
                {t.funnel.steps[s.milestone]}: {formatNumber(s.count, locale)}
              </li>
            ))}
          </ul>
        </Panel>
        <WonPaths data={data} />
      </div>
    </>
  );
}

export function FunnelPage() {
  const t = useT();
  usePageTitle(t.funnel.title);
  const [params] = useDashboardParams();
  const [search, setSearch] = useSearchParams();
  const mode: Mode = search.get('mode') === 'activity' ? 'activity' : 'cohort';
  const funnel = useFunnel(params, mode);

  return (
    <>
      <h1 className="text-2xl font-bold tracking-tight">{t.funnel.title}</h1>
      <Controls meta={funnel.data?.meta} compare={false} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{t.funnel.mode}</span>
        <SegmentedControl
          aria-label={t.funnel.mode}
          value={mode}
          onValueChange={(value) => {
            if (!value) return;
            setSearch(
              (current) => {
                const next = new URLSearchParams(current);
                next.set('mode', value);
                return next;
              },
              { replace: true },
            );
          }}
        >
          <SegmentedItem value="cohort">{t.funnel.cohort}</SegmentedItem>
          <SegmentedItem value="activity">{t.funnel.activity}</SegmentedItem>
        </SegmentedControl>
        <span className="text-xs text-muted-foreground">
          {mode === 'cohort' ? t.funnel.cohortHelp : t.funnel.activityHelp}
        </span>
      </div>
      <Async query={funnel}>
        {({ data }) =>
          data.mode === 'cohort' ? <CohortView data={data} /> : <ActivityView data={data} />
        }
      </Async>
    </>
  );
}
